import React, { useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { EarningType, Maybe } from "../../../__generated__/graphql";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import { getIsPay13thSalaryWithSalary } from "../../../utils";
import Gap from "../../../ui_atoms/Gap";
import VStack from "../../../ui_atoms/VStack";
import { BodySmallText, TitleMediumText } from "../../FormattedText";
import OptionsSelector, { OptionType } from "../../OptionsSelector";

export type ThirteenthSalaryChoiceValue = {
    has13thSalary: boolean;
    pay13thSalaryWithSalary: boolean;
};

type ThirteenthSalaryChoiceProps = {
    earningType?: Maybe<EarningType>;
    has13thSalary?: Maybe<boolean>;
    pay13thSalaryWithSalary?: Maybe<boolean>;
    /** The CCT makes the 13th mandatory (`getSalaryDetails.mandatory13thMonth`). */
    isMandatory: boolean;
    onChange: (value: ThirteenthSalaryChoiceValue) => void;
};

/**
 * The 13th salary questions of the salary screen:
 * - "Do you want a 13th salary?" (yes/no), unless the CCT makes it mandatory;
 * - for a monthly contract with a 13th: "Pay it in 12 or 13 times a year?" (12x/13x).
 *
 * A mandatory 13th of a non-monthly contract has no 12x/13x choice: the first question is then
 * kept, locked on "yes", so the user still sees that the CCT imposes it.
 */
const ThirteenthSalaryChoice: React.FC<ThirteenthSalaryChoiceProps> = ({
    earningType,
    has13thSalary,
    pay13thSalaryWithSalary,
    isMandatory,
    onChange,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();

    // A mandatory 13th counts as present even before `getSalaryDetails` echoed it on the contract.
    const hasThirteenth = isMandatory || !!has13thSalary;
    const isMonthly = earningType === EarningType.Monthly;
    // Resolved like the backend: without a 13th, or off monthly, it is always 12x.
    const is12x = getIsPay13thSalaryWithSalary({
        earningType,
        has13thSalary: hasThirteenth,
        pay13thSalaryWithSalary,
    });
    const showPresenceQuestion = !isMandatory || !isMonthly;
    const showRegimeQuestion = isMonthly && hasThirteenth;

    const presenceOptions = useMemo<OptionType<boolean>[]>(
        () => [
            { label: i18n.t("yes"), value: true },
            { label: i18n.t("no"), value: false },
        ],
        [i18n],
    );
    // The value is `pay13thSalaryWithSalary`: true for 12x, false for 13x.
    const regimeOptions = useMemo<OptionType<boolean>[]>(
        () => [
            {
                label: i18n.t("screens.user.create_mission_salary.thirteenth_salary_12x_option"),
                value: true,
            },
            {
                label: i18n.t("screens.user.create_mission_salary.thirteenth_salary_13x_option"),
                value: false,
            },
        ],
        [i18n],
    );

    // Switching the 13th off resets the choice to 12x, so switching it back on starts from 12x.
    const onSelectPresence = useCallback(
        (value: boolean) => {
            if (value !== hasThirteenth) {
                onChange({ has13thSalary: value, pay13thSalaryWithSalary: true });
            }
        },
        [hasThirteenth, onChange],
    );
    const onSelectRegime = useCallback(
        (value: boolean) => {
            if (value !== is12x) {
                onChange({ has13thSalary: true, pay13thSalaryWithSalary: value });
            }
        },
        [is12x, onChange],
    );

    const mandatoryNote = (
        <BodySmallText testID="thirteenth-salary-mandatory" color={colors.greyAccessible}>
            {i18n.t("screens.user.create_mission_salary.thirteenth_salary_mandatory_note")}
        </BodySmallText>
    );

    return (
        <VStack gap={1 / 2}>
            {showPresenceQuestion && (
                <View testID="thirteenth-salary-presence" style={styles.section}>
                    <TitleMediumText>
                        {i18n.t(
                            "screens.user.create_mission_salary.thirteenth_salary_presence_question",
                        )}
                    </TitleMediumText>
                    {isMandatory && mandatoryNote}
                    <Gap gap={1 / 3} />
                    <OptionsSelector<boolean>
                        shrinked
                        disabled={isMandatory}
                        value={hasThirteenth}
                        onSelect={onSelectPresence}
                        data={presenceOptions}
                    />
                </View>
            )}
            {showRegimeQuestion && (
                <View testID="thirteenth-salary-regime" style={styles.section}>
                    <TitleMediumText>
                        {i18n.t(
                            "screens.user.create_mission_salary.thirteenth_salary_regime_question",
                        )}
                    </TitleMediumText>
                    {isMandatory && mandatoryNote}
                    <Gap gap={1 / 3} />
                    <OptionsSelector<boolean>
                        shrinked
                        value={is12x}
                        onSelect={onSelectRegime}
                        data={regimeOptions}
                    />
                </View>
            )}
        </VStack>
    );
};

const useStyles = () => {
    const { colors, gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                // Laid out like the list rows of the salary screen: full width, closed by a line.
                section: {
                    marginHorizontal: -gridSize,
                    paddingHorizontal: gridSize,
                    paddingBottom: (2 / 3) * gridSize,
                    borderBottomWidth: 1,
                    borderColor: colors.accentLighter,
                },
            }),
        [colors.accentLighter, gridSize],
    );
};

export default ThirteenthSalaryChoice;
